import React from 'react';
import gql from 'graphql-tag';
import { Mutation } from 'react-apollo';
import { Alert } from 'reactstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import get from 'lodash/get';
import uuidv4 from 'uuid/v4';
import ErrorModal from 'components/ErrorModal';
import PhoneNumber from 'components/PhoneNumber';
import CreateBookingButton from './CreateBookingButton';
import BackButtonContainer from 'containers/BackButtonContainer';
import { getBookingEnginePath, isHostBookingSource } from 'utils/routeUtils';
import {
  PROPERTY_CODES,
  CALL_CENTER_PHONE_NUMBERS,
  PROPERTIES,
  FOOD_COMP,
  SPECIAL_OCCASION,
  SEND_AMENITIES
} from 'utils/constants';
import { getThemeName } from 'utils/themeUtils';
import { gaEvent } from 'utils/googleAnalytics';
import fragments from 'utils/graphql/fragments';
import { selectedOfferShape, submittedAccountShape } from 'utils/shapes';

const MUTATION_CREATE_BOOKING = gql`
  mutation CreateBooking($booking: CreateBooking!) {
    createBooking(booking: $booking) {
      hotelReservation {
        id
        reservationId
        roomStays {
          startDate
          endDate
          total {
            ...PriceFields
          }
          expectedCharges {
            totalRoomAndRatePackages
            totalTaxesAndFees
            taxInclusive
            decimals
          }
        }
      }
      validationError
      offerRedemptionError
      roomUnavailableError
    }
  }

  ${fragments.PriceFields}
`;

const MUTATION_CREATE_WAITLIST_BOOKING = gql`
  mutation CreateWaitlistBooking($booking: CreateHotelWaitlistBooking!) {
    createWaitlistBooking(booking: $booking) {
      success
    }
  }
`;

function CreateBooking(props) {
  const backButtonBeforeClick = e => {
    const { setShouldRefetch } = props;

    setShouldRefetch(true);
  };

  const handleBookButtonClick = createBooking => {
    const {
      startDate,
      endDate,
      property,
      selectedRoom,
      numberOfGuests,
      guestInfo,
      submittedAccount,
      selectedOffers,
      goToConfirmationPage,
      bookingSource,
      bookingComments,
      secondSubmittedAccount,
      isAdmin,
      isWaitListBooking,
      waitListBookingComment,
      waitListBookingReason,
      goToWaitlistConfirmationPage
    } = props;

    const isHostBooking = isHostBookingSource(bookingSource);
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    const alerts = get(guestInfo, 'alerts', []);
    const roomFeatures = get(guestInfo, 'roomFeatures', []);
    const traces = get(guestInfo, 'traces', []);
    const discounts = get(guestInfo, 'discounts', []);
    const specialRequests = get(guestInfo, 'specialRequests', []);
    const amenityRequests = get(guestInfo, 'amenityRequests', []);
    const customAmenityRequest = get(guestInfo, 'customAmenityRequest', '');

    // Include the selected quantity in each amenity selected,
    // and include the custom amenity if it's present.
    const amenities =
      amenityRequests && amenityRequests.length > 0
        ? amenityRequests.map(v => [v.value, v.quantity].join(', Quantity: '))
        : null;
    const amenitiesArray = customAmenityRequest
      ? [...(amenities || []), customAmenityRequest]
      : amenities;

    const booking = {
      booking: {
        propertyCode: PROPERTY_CODES[property],
        roomTypeCode: selectedRoom.roomType.roomTypeCodes.roomTypeCode,
        ratePlanCode:
          waitListBookingReason?.value === 'COMP'
            ? 'COMPHOST'
            : selectedRoom.roomRates[0].ratePlanCode,
        guestCount: numberOfGuests.value,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        creditCard: !(isHostBooking && isAdmin)
          ? {
              cardType: get(guestInfo, 'cardType.value'),
              cardHolderName: `${guestInfo.firstName} ${guestInfo.lastName}`,
              cardNumber: get(guestInfo, 'cardNumber')
                ? get(guestInfo, 'cardNumber')
                    .split(' ')
                    .join('')
                : null,
              cardCode: guestInfo.cvv,
              cardExpirationMonth: get(guestInfo, 'expirationMonth.value'),
              cardExpirationYear: get(guestInfo, 'expirationYear.value')
            }
          : null,
        guestProfile: {
          firstName: guestInfo.firstName,
          lastName: guestInfo.lastName,
          phone: guestInfo.phone,
          email: guestInfo.email,
          address: {
            addressLine: guestInfo.address,
            cityName: guestInfo.city,
            stateProv: guestInfo.state.value,
            countryCode: 'US',
            postalCode: guestInfo.postalCode
          }
        }
      },
      source: getThemeName(),
      bookingSource: bookingSource,
      additionalComments: bookingComments || null,
      alerts: alerts && alerts.length > 0 ? alerts.map(v => v.label) : null,
      roomFeatures:
        roomFeatures && roomFeatures.length > 0
          ? roomFeatures.map(v => v.value)
          : null,
      traces: traces && traces.length > 0 ? traces.map(v => v.label) : null,
      discounts:
        discounts && discounts.length > 0 ? discounts.map(v => v.label) : null,
      specialRequests:
        specialRequests && specialRequests.length > 0
          ? specialRequests.map(v => v.value)
          : null,
      amenityRequests: amenitiesArray
    };

    if (submittedAccount.accountNumber) {
      booking.patronAccountNumber = submittedAccount.accountNumber;
      booking.pin = submittedAccount.accountPin;
    }

    if (secondSubmittedAccount.accountNumber) {
      booking.secondPatronAccountNumber = secondSubmittedAccount.accountNumber;
    }

    if (selectedOffers && selectedOffers.length > 0) {
      booking.booking.promotionCodes = selectedOffers;
    }

    if (
      guestInfo.compCredit === FOOD_COMP &&
      guestInfo.creditAmount &&
      guestInfo.creditOutlet
    ) {
      booking.outletCredit =
        guestInfo.creditAmount + ' ' + guestInfo.creditOutlet;
    }

    if (
      guestInfo.specialOccasion === SPECIAL_OCCASION &&
      guestInfo.specialOccasionDetails
    ) {
      booking.specialOccasion = guestInfo.specialOccasionDetails;
    }

    let variables = { booking };

    if (isWaitListBooking) {
      variables = {
        booking: {
          createBooking: booking,
          externalConfirmationId: uuidv4(),
          waitlistRequestOptions: {
            waitlistReasonCode: get(waitListBookingReason, 'value', ''),
            waitlistReasonDescription: waitListBookingComment
          }
        }
      };
    }

    createBooking({ variables }).then(result => {
      if (get(result, 'data.createBooking.hotelReservation')) {
        const reservation = result.data.createBooking.hotelReservation;
        const total =
          reservation.roomStays &&
          reservation.roomStays[0] &&
          reservation.roomStays[0].total &&
          reservation.roomStays[0].total.amount;

        // Record GA event.
        gaEvent({
          category: 'Hotel Booking',
          action: 'Created Booking',
          label: `Property: ${PROPERTIES[property]}`,
          value: total
        });

        // Booking was successful, route to the confirmation page.
        goToConfirmationPage(result.data.createBooking);
      } else if (isWaitListBooking) {
        goToWaitlistConfirmationPage();
      }
    });
  };

  const { isWaitListBooking, property, guestInfo, page } = props;

  const compCreditFieldsEmpty =
    guestInfo?.compCredit === FOOD_COMP &&
    (!guestInfo?.creditAmount?.trim() || !guestInfo?.creditOutlet?.trim());
  const specialOccasionEmpty =
    guestInfo?.specialOccasion === SPECIAL_OCCASION &&
    !guestInfo?.specialOccasionDetails;
  const customAmenityExists =
    guestInfo.customAmenityRequest && guestInfo.customAmenityRequest.trim();
  const amenitiesEmpty =
    guestInfo?.sendAmenities === SEND_AMENITIES &&
    (!guestInfo?.amenityRequests || guestInfo?.amenityRequests.length === 0) &&
    !customAmenityExists;

  return (
    <Mutation
      mutation={
        isWaitListBooking
          ? MUTATION_CREATE_WAITLIST_BOOKING
          : MUTATION_CREATE_BOOKING
      }
    >
      {(createBooking, { loading, data, error }) => {
        const roomUnavailableError =
          data && data.createBooking && data.createBooking.roomUnavailableError;
        const bookingAlreadyExists =
          roomUnavailableError &&
          roomUnavailableError.includes('booking already exists');

        return (
          <React.Fragment>
            {error && (
              <ErrorModal
                property={property}
                isOpen={true}
                error={error}
                page={page}
              />
            )}
            {data &&
              data.createBooking &&
              data.createBooking.offerRedemptionError && (
                <ErrorModal property={property} isOpen={true} page={page}>
                  <p>An error has occurred while completing your booking.</p>
                  <p>
                    Please call{' '}
                    <PhoneNumber value={CALL_CENTER_PHONE_NUMBERS[property]} />{' '}
                    to complete your booking.
                  </p>
                </ErrorModal>
              )}

            {roomUnavailableError && (
              <ErrorModal
                property={property}
                isOpen={true}
                title="Room not available"
                page={page}
              >
                {bookingAlreadyExists ? (
                  <>
                    <p>
                      A booking for this patron already exists in the given date
                      range.
                    </p>
                    <p>
                      Please choose a different date range to complete your
                      booking.
                    </p>
                  </>
                ) : (
                  <>
                    <p>The room you have selected is no longer available.</p>
                    <p>
                      Please choose a different room to complete your booking.
                    </p>
                  </>
                )}
                <p>
                  If the issue persists, please call{' '}
                  <PhoneNumber value={CALL_CENTER_PHONE_NUMBERS[property]} /> to
                  complete your booking.
                </p>
                <BackButtonContainer
                  to={getBookingEnginePath('choose-accommodation')}
                  beforeNav={backButtonBeforeClick}
                >
                  Choose a different room
                </BackButtonContainer>
              </ErrorModal>
            )}

            {data &&
              data.createBooking &&
              data.createBooking.validationError && (
                <Alert color={'danger'}>
                  {data.createBooking.validationError}
                </Alert>
              )}

            <CreateBookingButton
              onClick={handleBookButtonClick}
              createBooking={createBooking}
              loading={loading}
              isWaitListBooking={isWaitListBooking}
              disabled={
                compCreditFieldsEmpty || specialOccasionEmpty || amenitiesEmpty
              }
            />
          </React.Fragment>
        );
      }}
    </Mutation>
  );
}

CreateBooking.propTypes = {
  guestInfo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    cardType: PropTypes.string,
    cardNumber: PropTypes.string,
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.number,
    cvv: PropTypes.number
  }),
  roomType: PropTypes.any,
  selectedRoom: PropTypes.any,
  numberOfGuests: PropTypes.any,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  property: PropTypes.any,
  submittedAccount: submittedAccountShape,
  selectedOffers: PropTypes.arrayOf(selectedOfferShape),
  setShouldRefetch: PropTypes.func,
  isAdmin: PropTypes.bool,
  bookingSource: PropTypes.string,
  bookingComments: PropTypes.string,
  secondSubmittedAccount: submittedAccountShape,
  isWaitListBooking: PropTypes.bool,
  waitListBookingReason: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }),
  waitListBookingComment: PropTypes.string,
  goToWaitlistConfirmationPage: PropTypes.func,
  isMobile: PropTypes.bool,
  page: PropTypes.shape({
    height: PropTypes.string,
    scrollY: PropTypes.string
  })
};

export default CreateBooking;
